/**
 * Coach post-session feedback question sets.
 *
 * MED-888 introduces a partner-coach question set alongside the existing Medley-coach
 * question set. Treated as configuration/data (not hardcoded UI) so additional variants
 * (per-partner, per-cohort) can be added later without a renderer rewrite.
 *
 * Storage: answers persist as JSON values in `coach_session_feedback.responses` (JSONB),
 * keyed by `Question.key`. The Medley-coach keys remain unchanged; partner-coach keys
 * are prefixed `partner_` so they coexist unambiguously in the same JSONB blob.
 */
export type CoachFeedbackQuestionType = 'textarea' | 'singleSelect';

export interface CoachFeedbackSingleSelectOption {
  value: number;
  label: string;
  description?: string;
}

export interface CoachFeedbackQuestion {
  key: string;
  shortLabel: string;
  questionText: string;
  type: CoachFeedbackQuestionType;
  required: boolean;
  charLimit?: number;
  helperText?: string;
  placeholder?: string;
  options?: CoachFeedbackSingleSelectOption[];
}

export interface CoachFeedbackQuestionSet {
  id: 'medley' | 'partner' | 'live_post_session';
  sectionHeader: (sessionNumber: number | null) => string;
  questions: CoachFeedbackQuestion[];
}

const MEDLEY_COACH_QUESTIONS: CoachFeedbackQuestion[] = [
  {
    key: 'session_insights',
    shortLabel: 'Session Insights',
    questionText: 'What are two to three insights that came up during the session?',
    type: 'textarea',
    required: true,
    placeholder: 'e.g., Breaking big tasks into small chunks...',
  },
  {
    key: 'what_worked_well',
    shortLabel: 'What Worked Well',
    questionText: 'What worked well in this session?',
    type: 'textarea',
    required: true,
    placeholder: 'e.g., Things you would do again...',
  },
  {
    key: 'improvements',
    shortLabel: 'Areas for Improvement',
    questionText: 'What ideas do you have for improvements next time?',
    type: 'textarea',
    required: true,
    placeholder: 'e.g., Suggested curriculum adjustments...',
  },
  {
    key: 'coach_self_insight',
    shortLabel: 'Coach Self-Insight',
    questionText: 'What insight did you gain for yourself as a coach?',
    type: 'textarea',
    required: true,
  },
  {
    key: 'share_with_medley',
    shortLabel: 'Share With Medley',
    questionText: 'Did anything happen in the session that you want to share with the Medley team?',
    type: 'textarea',
    required: false,
    placeholder: 'Any wins or issues to surface...',
  },
  {
    key: 'partner_themes',
    shortLabel: 'Partner Themes',
    questionText:
      'What, if any, themes or sentiments did you hear today that we should share back to the Partner?',
    type: 'textarea',
    required: false,
  },
  {
    key: 'support_needed',
    shortLabel: 'Support Needed',
    questionText:
      'What, if anything, do you need from Medley or the Partner before your next session?',
    type: 'textarea',
    required: false,
  },
];

const PARTNER_COACH_ENGAGEMENT_OPTIONS: CoachFeedbackSingleSelectOption[] = [
  {
    value: 1,
    label: 'Very disengaged',
    description: 'Cameras off, limited responses to prompts, distracted',
  },
  {
    value: 2,
    label: 'Below average engagement',
  },
  {
    value: 3,
    label: 'Average',
    description: 'Listening / following but not consistently participating actively',
  },
  {
    value: 4,
    label: 'Above average engagement',
  },
  {
    value: 5,
    label: 'Very engaged',
    description: 'Camera on, off mute, actively listening, asking follow-up questions',
  },
];

const PARTNER_COACH_QUESTIONS: CoachFeedbackQuestion[] = [
  {
    key: 'partner_what_worked_well',
    shortLabel: 'What worked well',
    questionText: 'What worked well in this session or resonated with your group?',
    type: 'textarea',
    required: true,
    charLimit: 1000,
    helperText: 'e.g., Things you would recommend other groups do, things you would do again',
  },
  {
    key: 'partner_ideas_for_improvement',
    shortLabel: 'Ideas for improvement',
    questionText: 'What ideas do you have for improvement in future sessions?',
    type: 'textarea',
    required: true,
    charLimit: 1000,
    helperText: 'e.g., suggested topic, a different agenda',
  },
  {
    key: 'partner_participant_insights',
    shortLabel: 'Participant insights',
    questionText:
      'What are two to three insights that came up for participants during the session?',
    type: 'textarea',
    required: true,
    charLimit: 1000,
    helperText:
      'e.g., I need to initiate conversations to guide my career; Preparation and focus in high-stakes moments leads to clearer communication',
  },
  {
    key: 'partner_insights_for_self',
    shortLabel: 'Insights for self',
    questionText: 'What insights have you gained for yourself as a mentor?',
    type: 'textarea',
    required: true,
    charLimit: 1000,
  },
  {
    key: 'partner_group_engagement',
    shortLabel: 'Group engagement',
    questionText:
      'On a scale of 1-5, how engaged were the participants that attended this session?',
    type: 'singleSelect',
    required: true,
    options: PARTNER_COACH_ENGAGEMENT_OPTIONS,
  },
  {
    key: 'partner_needs_before_next_session',
    shortLabel: 'Needs before next session',
    questionText: 'What, if anything, do you need before your next session?',
    type: 'textarea',
    required: false,
    charLimit: 1000,
  },
  {
    key: 'partner_anything_else',
    shortLabel: 'Anything else',
    questionText: "Is there anything else you'd like to share about your session?",
    type: 'textarea',
    required: false,
    charLimit: 1000,
  },
];

export const medleyCoachQuestionSet: CoachFeedbackQuestionSet = {
  id: 'medley',
  sectionHeader: () => 'Session Reflection',
  questions: MEDLEY_COACH_QUESTIONS,
};

/**
 * MED-794 (2.6a) introduced the right-rail "Medley Feedback Form" on the live
 * coach post-session screen with placeholder fields. MED-988 (2.6e) converges
 * it onto Medley's real Session Highlights content: it reuses the seven
 * `medley` coach questions verbatim (same keys/labels/placeholders), then
 * appends the SGOS-only `carryover_for_next_session` field last — a
 * forward-looking field distinct from the current-session reflection set and
 * not part of Brook's Highlights form.
 *
 * Storage is the same `coach_session_feedback.responses` JSONB blob used by the
 * other question sets — keys coexist in one merged row. The set keeps its own
 * "Medley Feedback Form" section header (the `medley` set's header is
 * "Session Reflection" for the separate reflection surface, so we do not
 * mutate it here).
 *
 * AI pre-population (MED-893 / 2.6b, re-mapped in 2.6e): `session_insights` and
 * `carryover_for_next_session` are seeded from the recap-draft payload — see
 * `MedleyFeedbackFormCard`.
 */
const LIVE_POST_SESSION_CARRYOVER_QUESTION: CoachFeedbackQuestion = {
  key: 'carryover_for_next_session',
  shortLabel: 'Carryover for next session',
  questionText: 'Carryover for next session',
  type: 'textarea',
  required: false,
  helperText: 'Surfaces in your next pre-session guidance.',
};

const LIVE_POST_SESSION_COACH_QUESTIONS: CoachFeedbackQuestion[] = [
  ...MEDLEY_COACH_QUESTIONS,
  LIVE_POST_SESSION_CARRYOVER_QUESTION,
];

export const liveSessionCoachQuestionSet: CoachFeedbackQuestionSet = {
  id: 'live_post_session',
  sectionHeader: () => 'Medley Feedback Form',
  questions: LIVE_POST_SESSION_COACH_QUESTIONS,
};

export const partnerCoachQuestionSet: CoachFeedbackQuestionSet = {
  id: 'partner',
  sectionHeader: (sessionNumber) =>
    sessionNumber !== null && sessionNumber !== undefined
      ? `Session ${sessionNumber} Highlights`
      : 'Session Highlights',
  questions: PARTNER_COACH_QUESTIONS,
};

/**
 * Resolve the question set to display for a given coach type.
 * Currently two variants; extend here when per-cohort / per-partner variants ship.
 */
export function getCoachFeedbackQuestionSet(
  coachType: string | null | undefined,
): CoachFeedbackQuestionSet {
  return coachType === 'partner' ? partnerCoachQuestionSet : medleyCoachQuestionSet;
}

/**
 * Build a Map<key, shortLabel> for one or more question sets — useful for CM-facing
 * label resolution (e.g., REFLECTION_LABELS in slide-out / Data Library tooltip lookup).
 */
export function buildShortLabelMap(
  ...sets: CoachFeedbackQuestionSet[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const set of sets) {
    for (const q of set.questions) {
      out[q.key] = q.shortLabel;
    }
  }
  return out;
}

/**
 * Build a Map<key, full question text> for tooltip / hover display in CM-facing views.
 */
export function buildFullQuestionTextMap(
  ...sets: CoachFeedbackQuestionSet[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const set of sets) {
    for (const q of set.questions) {
      out[q.key] = q.questionText;
    }
  }
  return out;
}

/**
 * Look up the engagement-scale label for a numeric value (used by CM-facing views
 * displaying the saved 1-5 value).
 */
export function getPartnerEngagementLabel(value: number | string | null | undefined): string {
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (typeof numeric !== 'number' || Number.isNaN(numeric)) return '—';
  const option = PARTNER_COACH_ENGAGEMENT_OPTIONS.find((o) => o.value === numeric);
  return option ? `${numeric} — ${option.label}` : String(numeric);
}
